import { strings } from "@perceptron/constants";
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import ReactDOM from "react-dom/client";

import { CustomPlaceholder } from "@demo/components/CustomPlaceholder";
import { isMobileViewport } from "@demo/hooks/useIsMobile";

export interface EditorPlaceholderExtensionOptions {
  placeholder: string;
  showOnlyWhenEditable: boolean;
  showOnlyCurrent: boolean;
  includeChildren: boolean;
}

// Map to store React roots for cleanup
const rootsMap = new Map<HTMLElement, ReactDOM.Root>();

export const EditorPlaceholderExtension = Extension.create<EditorPlaceholderExtensionOptions>({
  name: "editorPlaceholder",

  addOptions() {
    return {
      placeholder: strings.editor.placeholders.default,
      showOnlyWhenEditable: true,
      showOnlyCurrent: true,
      includeChildren: false,
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("editorPlaceholder"),
        props: {
          decorations: ({ doc, selection }) => {
            const { anchor } = selection;
            const decorations: Decoration[] = [];

            const isMobile = isMobileViewport();

            // On mobile, the placeholder logic should always be evaluated.
            // On desktop, it depends on the editor's editable state.
            const active = this.editor.isEditable || !this.options.showOnlyWhenEditable || isMobile;

            if (!active) {
              return DecorationSet.empty;
            }

            doc.descendants((node, pos) => {
              const hasAnchor = anchor >= pos && anchor <= pos + node.nodeSize;

              // Check if this is a paragraph node
              if (node.type.name !== "paragraph") {
                return this.options.includeChildren;
              }

              // Check if paragraph is truly empty (no content or only whitespace)
              const isEmpty = node.content.size === 0 || node.textContent.trim() === "";

              // Determine if placeholder should show
              let shouldShow = false;

              shouldShow = (hasAnchor || !this.options.showOnlyCurrent) && isEmpty;

              if (shouldShow) {
                const decoration = Decoration.widget(
                  pos,
                  () => {
                    const container = document.createElement("span");
                    container.className = "react-placeholder-container";
                    container.style.position = "absolute";
                    container.style.pointerEvents = "none";
                    container.style.userSelect = "none";
                    container.setAttribute("aria-hidden", "true");
                    container.setAttribute("role", "presentation");

                    // Create or reuse React root
                    let root = rootsMap.get(container);
                    if (!root) {
                      root = ReactDOM.createRoot(container);
                      rootsMap.set(container, root);
                    }

                    // Render the placeholder component
                    root.render(<CustomPlaceholder text={this.options.placeholder} />);

                    // Clean up on removal using MutationObserver
                    const setupCleanupObserver = () => {
                      if (!container.parentNode) return;

                      const observer = new MutationObserver((mutations) => {
                        mutations.forEach((mutation) => {
                          mutation.removedNodes.forEach((node) => {
                            if (node === container || (node as Element).contains?.(container)) {
                              const root = rootsMap.get(container);
                              if (root) {
                                root.unmount();
                                rootsMap.delete(container);
                              }
                              observer.disconnect();
                            }
                          });
                        });
                      });

                      observer.observe(container.parentNode, {
                        childList: true,
                        subtree: true,
                      });
                    };

                    // Set up the observer after the container is added to DOM
                    if (container.parentNode) {
                      setupCleanupObserver();
                    } else {
                      // Wait for the container to be added to DOM
                      const connectObserver = new MutationObserver(() => {
                        if (container.parentNode) {
                          setupCleanupObserver();
                          connectObserver.disconnect();
                        }
                      });
                      connectObserver.observe(document.body, {
                        childList: true,
                        subtree: true,
                      });
                    }

                    return container;
                  },
                  {
                    side: 0,
                    // Widgets with equal keys are treated as identical across
                    // transactions, so the DOM container, React root, and
                    // cleanup observers are created once per appearance
                    // instead of on every keystroke/selection change.
                    key: `editor-placeholder-${pos}-${this.options.placeholder}`,
                  },
                );

                decorations.push(decoration);
              }

              return this.options.includeChildren;
            });

            return DecorationSet.create(doc, decorations);
          },
        },
      }),
    ];
  },

  onDestroy() {
    rootsMap.forEach((root) => {
      root.unmount();
    });
    rootsMap.clear();
  },
});
